import React, { useEffect, useMemo } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, MessageCircle, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { SnackbarsProps, SnackbarVariant } from './snackbars.types';

/**
 * Get icon for each variant - matches mobile pattern
 */
const getVariantIcon = (variant: SnackbarVariant) => {
    switch (variant) {
        case 'success':
            return CheckCircle;
        case 'error':
            return XCircle;
        case 'warning':
            return AlertTriangle;
        case 'info':
            return Info;
        default:
            return MessageCircle;
    }
};

/**
 * Get background color class for each variant - matches mobile pattern
 */
const getVariantBgClass = (variant: SnackbarVariant) => {
    switch (variant) {
        case 'success':
            return 'bg-[var(--color-success-10)] border-[var(--color-success-50)]';
        case 'error':
            return 'bg-[var(--color-error-10)] border-[var(--color-error-50)]';
        case 'warning':
            return 'bg-[var(--color-warning-10)] border-[var(--color-warning-50)]';
        case 'info':
            return 'bg-[var(--color-info-10)] border-[var(--color-info-50)]';
        default:
            return 'bg-[var(--color-neutral-10)] border-[var(--color-neutral-50)]';
    }
};

/**
 * Get icon color class for each variant
 */
const getVariantIconClass = (variant: SnackbarVariant) => {
    switch (variant) {
        case 'success':
            return 'text-[var(--color-success-50)]';
        case 'error':
            return 'text-[var(--color-error-50)]';
        case 'warning':
            return 'text-[var(--color-warning-50)]';
        case 'info':
            return 'text-[var(--color-info-50)]';
        default:
            return 'text-[var(--color-neutral-50)]';
    }
};

/**
 * Snackbars Component - Matches mobile ui-react-native-core API
 *
 * Supports:
 * - variant: default, success, error, warning, info
 * - position: top, bottom
 * - action: label + onPress
 * - custom icon
 * - auto-dismiss with duration
 */
const Snackbars: React.FC<SnackbarsProps> = ({
    message,
    type,
    variant = 'default',
    isVisible,
    duration = 3000,
    onClose,
    className,
    testID = 'snackbars',
    action,
    icon: customIcon,
    position = 'bottom',
}) => {
    // Support legacy type prop, prefer variant
    const finalVariant: SnackbarVariant = variant !== 'default' ? variant : (type as SnackbarVariant) || 'default';

    useEffect(() => {
        if (isVisible && duration > 0) {
            const timer = setTimeout(() => onClose?.(), duration);
            return () => clearTimeout(timer);
        }
    }, [isVisible, duration, onClose]);

    const Icon = useMemo(() => getVariantIcon(finalVariant), [finalVariant]);
    const bgClass = useMemo(() => getVariantBgClass(finalVariant), [finalVariant]);
    const iconClass = useMemo(() => getVariantIconClass(finalVariant), [finalVariant]);

    const positionClass = useMemo(() => (position === 'top' ? 'top-4 bottom-auto' : 'bottom-4 top-auto'), [position]);

    const handleActionPress = () => {
        action?.onPress();
        onClose?.();
    };

    if (!isVisible) return null;

    return (
        <div
            className={cn(
                'fixed left-1/2 -translate-x-1/2 z-[999999] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border max-w-md min-w-[280px]',
                'text-[var(--color-neutral-90)]',
                bgClass,
                positionClass,
                className
            )}
            data-testid={testID}
            role="alert"
            aria-live="polite"
        >
            {/* Icon */}
            {customIcon || <Icon className={cn('w-5 h-5 flex-shrink-0', iconClass)} />}

            {/* Message */}
            <span className="flex-1 text-sm font-medium">{message}</span>

            {/* Action Button (optional) */}
            {action && (
                <button
                    onClick={handleActionPress}
                    className="text-[var(--color-primary-50)] font-semibold text-sm hover:opacity-80 transition-opacity"
                >
                    {action.label}
                </button>
            )}

            {/* Close Button */}
            {onClose && (
                <button
                    onClick={onClose}
                    className="text-[var(--color-neutral-70)] hover:opacity-80 transition-opacity"
                    aria-label="Close notification"
                >
                    <X className="w-5 h-5" />
                </button>
            )}
        </div>
    );
};

export default Snackbars;
